import { OnboardingAppData } from "./OnboardingPortal.types";
import { getCompanyIdFromName } from "./OnboardingPortal.utils";
import * as XLSX from "xlsx";

type ExportableAppData = OnboardingAppData & {
    keywords?: string[];
    companiesAllowed?: string[];
    companiesRestricted?: string[];
};

const EXPORT_HEADERS: string[] = [
    "synKeywords",
    "id",
    "alias",
    "subTitle",
    "icon",
    "title",
    "target",
    "description",
    "Allowed",
    "Restricted",
];

const EXCEL_EXPORT_HEADERS: string[] = [
    "synKeywords",
    "id",
    "alias",
    "subTitle",
    "icon",
    "title",
    "target",
    "description",
    "allowed_companies",
    "denied_companies",
    "type",
];

const APP_ROW_TYPE = "app";

const QNA_EXPORT_HEADERS: string[] = [
    "question",
    "answer",
    "category",
    "Allowed",
    "Restricted",
];

const sanitizeFileName = (value: string): string => {
    return value
        .trim()
        .replace(/[^a-zA-Z0-9-_]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase();
};

const serializeCellValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return "";
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => String(item).normalize("NFC").trim())
            .filter((item) => item.length > 0)
            .join(", ");
    }

    if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
    ) {
        return String(value).normalize("NFC");
    }

    return String(value).normalize("NFC");
};

const companiesListToString = (list: string[] | undefined): string => {
    if (!list || list.length === 0) {
        return "";
    }

    const companyNames = list
        .map((name) => String(name).trim())
        .filter((name) => name.length > 0)
        .map((name) => (name.includes(",") ? `'${name}'` : name));

    return companyNames.join(", ");
};

const companiesListToIdString = (list: string[] | undefined): string => {
    if (!list || list.length === 0) {
        return "";
    }

    const companyIds = list
        .map((name) => String(name).trim())
        .filter((name) => name.length > 0)
        .map((name) => getCompanyIdFromName(name) ?? name)
        .map((id) => (id.includes(",") ? `'${id}'` : id));

    return companyIds.join(", ");
};

const escapeCsvCell = (value: string): string => {
    const escapedValue = value.replace(/"/g, '""');
    return `"${escapedValue}"`;
};

const getExportRowValues = (appData: ExportableAppData): string[] => {
    return [
        serializeCellValue(appData?.keywords),
        serializeCellValue(appData?.appId || appData?.id),
        serializeCellValue(appData?.alias),
        serializeCellValue(appData?.subTitle),
        serializeCellValue(appData?.icon),
        serializeCellValue(appData?.title),
        serializeCellValue(appData?.url),
        serializeCellValue(appData?.tooltip),
        serializeCellValue(companiesListToIdString(appData?.companiesAllowed)),
        serializeCellValue(
            companiesListToIdString(appData?.companiesRestricted)
        ),
        // serializeCellValue(appData?.tooltip),
    ];
};

const getTabularRows = (appDataList: OnboardingAppData[]): string[][] => {
    return appDataList.map((appData) => getExportRowValues(appData));
};

const getExcelTabularRows = (appDataList: OnboardingAppData[]): string[][] => {
    return getTabularRows(appDataList).map((rowValues) => [
        ...rowValues,
        APP_ROW_TYPE,
    ]);
};

const getCsvRows = (appDataList: OnboardingAppData[]): string[] => {
    const rows = getTabularRows(appDataList).map((rowValues) =>
        rowValues.map(escapeCsvCell).join(",")
    );

    return [EXPORT_HEADERS.map(escapeCsvCell).join(","), ...rows];
};

export const exportAppsDataAsCsv = (
    appDataList: OnboardingAppData[],
    fileName = "onboarding-apps"
): void => {
    if (!Array.isArray(appDataList) || appDataList.length === 0) {
        return;
    }

    const csvContent = `\uFEFF${getCsvRows(appDataList).join("\n")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const downloadUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = downloadUrl;

    const safeFileName = sanitizeFileName(fileName) || "onboarding-apps";
    link.download = `${safeFileName}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(downloadUrl);
};

export const exportAppsDataAsExcel = (
    appDataList: OnboardingAppData[],
    fileName = "onboarding-apps"
): void => {
    if (!Array.isArray(appDataList) || appDataList.length === 0) {
        return;
    }

    const rows = getExcelTabularRows(appDataList);
    const worksheet = XLSX.utils.aoa_to_sheet([EXCEL_EXPORT_HEADERS, ...rows]);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "App Data");

    const safeFileName = sanitizeFileName(fileName) || "onboarding-apps";
    XLSX.writeFile(workbook, `${safeFileName}.xlsx`);
};

export type OnboardingQnAData = {
    question?: unknown;
    answer?: unknown;
    companiesAllowed?: string[];
    companiesRestricted?: string[];
    category?: string;
};

const getQnAExportRowValues = (qnaData: OnboardingQnAData): string[] => {
    return [
        serializeCellValue(qnaData?.question),
        serializeCellValue(qnaData?.answer),
        serializeCellValue(qnaData?.category),
        serializeCellValue(companiesListToString(qnaData?.companiesAllowed)),
        serializeCellValue(companiesListToString(qnaData?.companiesRestricted)),
    ];
};

const getQnATabularRows = (qnaDataList: OnboardingQnAData[]): string[][] => {
    return qnaDataList.map((qnaData) => getQnAExportRowValues(qnaData));
};

const getQnACsvRows = (qnaDataList: OnboardingQnAData[]): string[] => {
    const rows = getQnATabularRows(qnaDataList).map((rowValues) =>
        rowValues.map(escapeCsvCell).join(",")
    );

    return [QNA_EXPORT_HEADERS.map(escapeCsvCell).join(","), ...rows];
};

export const exportQnADataAsCsv = (
    qnaDataList: OnboardingQnAData[],
    fileName = "onboarding-qna"
): void => {
    if (!Array.isArray(qnaDataList) || qnaDataList.length === 0) {
        return;
    }

    const csvContent = `\uFEFF${getQnACsvRows(qnaDataList).join("\n")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const downloadUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = downloadUrl;

    const safeFileName = sanitizeFileName(fileName) || "onboarding-qna";
    link.download = `${safeFileName}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(downloadUrl);
};

export const exportQnADataAsExcel = (
    qnaDataList: OnboardingQnAData[],
    fileName = "onboarding-qna"
): void => {
    if (!Array.isArray(qnaDataList) || qnaDataList.length === 0) {
        return;
    }

    const rows = getQnATabularRows(qnaDataList);
    const worksheet = XLSX.utils.aoa_to_sheet([QNA_EXPORT_HEADERS, ...rows]);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "QnA Data");

    const safeFileName = sanitizeFileName(fileName) || "onboarding-qna";
    XLSX.writeFile(workbook, `${safeFileName}.xlsx`);
};
